import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { postAgentTurn, agentErrorCode } from "@/lib/agent-request";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  isError?: boolean;
}

/** Build a chat message with a fresh id + timestamp. */
function makeMessage(role: ChatMessage["role"], content: string, isError = false): ChatMessage {
  return {
    id: crypto.randomUUID(),
    role,
    content,
    timestamp: new Date(),
    ...(isError ? { isError: true } : {}),
  };
}

/**
 * Help-chat hook (TES-1128). Talks to the `help-chat` Edge Function, which runs
 * the same `agent-orchestrator` handler with `feature: "help-chat"` — a single
 * user-JWT-scoped JSON request/response (NOT a stream):
 *   POST {VITE_SUPABASE_URL}/functions/v1/help-chat
 *   body: { message, sessionId?, language }
 *   200 → { sessionId, response }   4xx/5xx → { error }
 */
export function useHelpChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const sessionIdRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { i18n, t } = useTranslation();

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Resolve a non-OK response to a thrown, translated error. Mirrors
  // useAgentChat.errorText: map the status to a stable code first, then
  // translate — so internal codes like "not_authenticated" never surface as
  // bubble text. Unmapped codes fall back to the server message, then generic.
  const httpError = useCallback(
    (status: number, serverMessage?: string): Error => {
      const map: Record<string, string> = {
        not_authenticated: t("helpChat.errors.notAuthenticated"),
        no_provider: t("helpChat.errors.noProvider"),
        rate_limited: t("helpChat.errors.rateLimited"),
      };
      const code = agentErrorCode(status);
      return new Error(map[code] ?? serverMessage ?? t("helpChat.errors.generic"));
    },
    [t],
  );

  const sendMessage = useCallback(
    async (content: string) => {
      setMessages((prev) => [...prev, makeMessage("user", content)]);
      setIsLoading(true);

      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const { ok, status, payload } = await postAgentTurn(
          "help-chat",
          {
            message: content,
            sessionId: sessionIdRef.current || undefined,
            language: i18n.language || "de",
          },
          abortController.signal,
        );

        if (!ok) throw httpError(status, payload.error);
        if (payload.sessionId) sessionIdRef.current = payload.sessionId;
        setMessages((prev) => [...prev, makeMessage("assistant", payload.response ?? "")]);
      } catch (err) {
        if ((err as Error)?.name === "AbortError") return;
        const text = err instanceof Error ? err.message : t("helpChat.errors.generic");
        setMessages((prev) => [...prev, makeMessage("assistant", text, true)]);
      } finally {
        setIsLoading(false);
      }
    },
    [i18n.language, t, httpError],
  );

  const clearChat = useCallback(() => {
    abortControllerRef.current?.abort();
    setMessages([]);
    sessionIdRef.current = null;
  }, []);

  return { messages, isLoading, sendMessage, clearChat };
}
